import { makeStyles } from 'tss-react/mui';
import {
  Root as DropdownMenuRoot,
  Trigger,
  Portal,
  Group,
  Label,
  Arrow,
  Item,
} from '@radix-ui/react-dropdown-menu';
import React, { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Box } from '@mui/material';
import palette from '@ui/palette';
import Button from '@ui/Button';
import Typography from '@ui/Typography';
import { useProductUsageQuery } from 'components/ProductUsage/operation.generated';
import { useUserSubscription } from 'components/Account/operation.generated';
import {
  getDownloadMinutesCounterDisplay,
  getProductUsageAccessScope,
} from 'components/Account/Usage/downloadMinutesUtils';
import { useIsTrialUser } from 'context/user/UserContext';
import { MenuContent } from '../../DropdownMenu/components';
import CounterButton from './CounterButton';
import usePromptForSubscriptionUpgrade from '../usePromptForSubscriptionUpgrade';
import theme from '../../../theme';

const useStyles = makeStyles()(() => ({
  content: {
    // Needed to overlay menu on top of the navbar itself
    zIndex: theme.zIndex.tooltip,
    maxWidth: 'calc(18vw)',
    minWidth: '200px',
    backgroundColor: palette.gray_900,
    '& div[role="menuitem"]': {
      '&:not([data-disabled=""])': {
        backgroundColor: palette.gray_900,
        '&[data-highlighted]': {
          backgroundColor: palette.gray_700,
        },
      },
    },
  },
  arrow: {
    fill: palette.gray_900,
  },
  primaryAction: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    padding: '4px 8px',
    cursor: 'pointer',
    backgroundColor: theme.palette.action.hover,
    width: 'fit-content',
    '&:hover': {
      backgroundColor: palette.gray_800,
      color: theme.palette.primary.light,
    },
  },
  counterContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'right',
    padding: theme.spacing(2),
  },
  counterActionButton: {
    width: theme.spacing(10),
  },
}));

export interface CounterDropdownMenuProps {
  disablePortal?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export interface CounterDropdownMenuContentProps {
  planDisplayName: string;
  planInterval: string;
  planLimit: number;
  planUsage: number;
  showUpgrade?: boolean;
  onUpgrade?: () => void;
}

const CounterDropdownMenuContent = React.forwardRef<
  HTMLDivElement,
  CounterDropdownMenuContentProps
>((props, ref) => {
  const {
    showUpgrade,
    onUpgrade,
    planDisplayName,
    planInterval,
    planLimit,
    planUsage,
  } = props;
  const { classes } = useStyles();

  const planLimitMinutes = planLimit / 60000;

  const planRemainingMs = Math.max(0, planLimit - (planUsage ?? 0));
  const planRemainingMinutes = Math.floor(planRemainingMs / 60000);
  const planRemainingSeconds = Math.ceil((planRemainingMs % 60000) / 1000);
  let planIntervalFormatted = planInterval.toLowerCase();

  if (planIntervalFormatted === 'year') {
    planIntervalFormatted = 'yr';
  } else if (planIntervalFormatted === 'month') {
    planIntervalFormatted = 'mo';
  } else if (planIntervalFormatted === 'week') {
    planIntervalFormatted = 'wk';
  }

  return (
    <MenuContent
      ref={ref}
      id="product-usage-counter-menu"
      className={classes.content}
      side="bottom"
      sideOffset={4}
      collisionPadding={4}
      aria-label={`Plan usage menu`}
    >
      <Group>
        <Label className={classes.counterContainer}>
          <Typography
            variant="h4"
            component="span"
            aria-label={`${planDisplayName} is your current subscription`}
          >
            {`${planDisplayName}`}
          </Typography>
          <Typography
            variant="h5"
            component="span"
            color="secondary"
            aria-label={`${planRemainingMinutes} downloaded minutes remaining this ${planIntervalFormatted}`}
          >
            {`${planLimitMinutes} downloaded minutes / ${planIntervalFormatted}`}
          </Typography>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            marginTop={theme.spacing(1.5)}
          >
            <Typography
              variant="h5"
              component="span"
              aria-label={`${planDisplayName} is your current subscription`}
            >
              {`Minutes remaining`}
            </Typography>
            <Typography
              variant="h5"
              component="span"
              bold
              aria-label={`${planDisplayName} is your current subscription`}
            >
              {`${planRemainingMinutes.toString()}:${planRemainingSeconds
                .toString()
                .padStart(2, '0')}`}
            </Typography>
          </Box>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            marginTop={theme.spacing(1.5)}
          >
            <Item asChild>
              <Button
                variant="compact"
                subvariant="secondary"
                className={classes.counterActionButton}
              >
                <Typography variant="pSm" color="inherit" inline>
                  <Link
                    href="/dashboard/account/subscription"
                    style={{ textDecoration: 'none' }}
                  >
                    See details
                  </Link>
                </Typography>
              </Button>
            </Item>
            {showUpgrade && (
              <Item asChild onSelect={onUpgrade}>
                <Button
                  variant="compact"
                  subvariant="secondary"
                  className={classes.counterActionButton}
                >
                  <Typography variant="pSm" color="action" inline>
                    Upgrade
                  </Typography>
                </Button>
              </Item>
            )}
          </Box>
        </Label>
      </Group>
      <Arrow className={classes.arrow} />
    </MenuContent>
  );
});

export { CounterDropdownMenuContent };

const CounterDropdownMenu = (props: CounterDropdownMenuProps) => {
  const { disablePortal = false, onOpenChange, ...rest } = props;
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const { data: subscriptionData } = useUserSubscription();
  const { data: productUsageData } = useProductUsageQuery();

  const planDisplayName =
    subscriptionData?.user.subscription?.plan.displayName || '';
  const planInterval = subscriptionData?.user.subscription?.plan.interval || '';

  const upgradePath = 'Dashboard > Counter Dropdown Menu';

  const counterDisplay = (() => {
    if (!productUsageData) {
      return null;
    }

    const { productAccessLimits, productAccessPeriodUsage } =
      getProductUsageAccessScope(productUsageData.user);

    return getDownloadMinutesCounterDisplay(
      productAccessLimits,
      productAccessPeriodUsage
    );
  })();

  const productUsage = counterDisplay
    ? {
        limit: counterDisplay.limit,
        usage: counterDisplay.usage,
      }
    : undefined;

  const isTrialUser = useIsTrialUser();
  const showUpgrade =
    !subscriptionData?.user.subscription?.plan?.managed && !isTrialUser;

  const handleTriggerClick = useCallback(() => {
    setOpen(!open);
    if (onOpenChange) onOpenChange(!open);
  }, [onOpenChange, open]);

  /**
   * Because the user profile menu's tooltip has nav links,
   * We should close the tooltip when we redirect user to those pages
   */
  useEffect(() => {
    const routeChanged = () => {
      setOpen(false);
      if (onOpenChange) onOpenChange(false);
    };

    router?.events.on('routeChangeComplete', routeChanged);

    return () => {
      router?.events.off('routeChangeComplete', routeChanged);
    };
  }, [onOpenChange, router?.events, router?.route]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (onOpenChange) onOpenChange(isOpen);
  };

  const [openUpgradePrompt] = usePromptForSubscriptionUpgrade({
    upgradeContext: 'increase_access',
    upgradePath,
  });

  if (counterDisplay && counterDisplay.limitMs < 0) {
    return null;
  }

  return counterDisplay ? (
    <DropdownMenuRoot open={open} onOpenChange={handleOpenChange}>
      <Trigger asChild onClick={handleTriggerClick}>
        <CounterButton open={open} productUsage={productUsage} />
      </Trigger>
      {disablePortal && (
        <CounterDropdownMenuContent
          planDisplayName={planDisplayName}
          planInterval={planInterval}
          planLimit={counterDisplay.limitMs}
          planUsage={counterDisplay.usageMs}
          showUpgrade={showUpgrade}
          onUpgrade={openUpgradePrompt}
          {...rest}
        />
      )}
      {!disablePortal && (
        <Portal>
          <CounterDropdownMenuContent
            planDisplayName={planDisplayName}
            planInterval={planInterval}
            planLimit={counterDisplay.limitMs}
            planUsage={counterDisplay.usageMs}
            showUpgrade={showUpgrade}
            onUpgrade={openUpgradePrompt}
            {...rest}
          />
        </Portal>
      )}
    </DropdownMenuRoot>
  ) : null;
};

export default CounterDropdownMenu;
